import { baseApi } from "~/app/store/baseApi";
import type { AsmState, LoginRequest, PersonifyRequest } from "~/features/assistedService/types";

const ASM_BASE = "/assisted-service";

/** Tag id identifying the single current ASM session state read by the bar. */
const ASM_STATE_TAG = { type: "AsmState", id: "current" } as const;

/**
 * RTK Query endpoints for the ASM JSON API. The {@code getState} query is the source of truth for
 * the bar's state; every successful mutation invalidates it so the bar re-syncs. Mutations that
 * return {@code redirectUrl}/{@code reload} still drive navigation via the hook's {@code applyResult}.
 */
export const asmApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getState: builder.query<AsmState, void>({
      query: () => ({ url: `${ASM_BASE}/state` }),
      providesTags: [ASM_STATE_TAG],
    }),
    login: builder.mutation<AsmState, LoginRequest>({
      query: (body) => ({ url: `${ASM_BASE}/login`, method: "POST", body }),
      invalidatesTags: [ASM_STATE_TAG],
    }),
    logout: builder.mutation<AsmState, void>({
      query: () => ({ url: `${ASM_BASE}/logoutasm`, method: "POST" }),
      invalidatesTags: [ASM_STATE_TAG],
    }),
    personify: builder.mutation<AsmState, PersonifyRequest>({
      query: (body) => ({ url: `${ASM_BASE}/personify-customer`, method: "POST", body }),
      invalidatesTags: [ASM_STATE_TAG],
    }),
    personifyStop: builder.mutation<AsmState, void>({
      query: () => ({ url: `${ASM_BASE}/personify-stop`, method: "POST" }),
      invalidatesTags: [ASM_STATE_TAG],
    }),
    refresh: builder.mutation<AsmState, void>({
      query: () => ({ url: `${ASM_BASE}/refresh`, method: "POST" }),
      invalidatesTags: [ASM_STATE_TAG],
    }),
    quit: builder.mutation<void, void>({
      query: () => ({ url: `${ASM_BASE}/quit`, method: "POST" }),
      invalidatesTags: [ASM_STATE_TAG],
    }),
    resetSession: builder.mutation<void, void>({
      query: () => ({ url: `${ASM_BASE}/resetSession`, method: "POST" }),
    }),
  }),
});

export const {
  useGetStateQuery,
  useLoginMutation,
  useLogoutMutation,
  usePersonifyMutation,
  usePersonifyStopMutation,
  useRefreshMutation,
  useQuitMutation,
  useResetSessionMutation,
} = asmApi;
